/**
 * Properties sent on EVERY event (Event_Properties sheet):
 *   lead_id, session_id, channel, platform  (+ stage, added by track.js from the schema)
 *
 * Values set via `setCommonProps()` win; otherwise they are resolved from
 * existing app state at send time, so they survive full page reloads.
 */
import { v4 as uuidv4 } from 'uuid';
import { getLeadId } from '../services/journey';
import { resolveUserType } from '../lib/userTypeParam';
import { readUserState } from './context';

export const PLATFORM = 'WEB';
const SESSION_KEY = 'ekyc_moe_session_id';

/** URL / redux user type → sheet channel value. */
const CHANNEL_BY_USER_TYPE = Object.freeze({
    direct: 'DAD',
    crm: 'Branch',
    pp: 'Franchise',
    alliance: 'Alliance',
});

/** @type {{ lead_id?: string, channel?: string }} */
let overrides = {};
let memorySessionId = null;

/**
 * One id per browser tab session (sessionStorage), generated on first use.
 * Falls back to an in-memory id when storage is blocked.
 */
export const getAnalyticsSessionId = () => {
    try {
        let id = window.sessionStorage.getItem(SESSION_KEY);
        if (!id) {
            id = uuidv4();
            window.sessionStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch (_) {
        if (!memorySessionId) memorySessionId = uuidv4();
        return memorySessionId;
    }
};

/** True when this tab session already has an analytics session id. */
export const hasAnalyticsSession = () => {
    try {
        return !!(window.sessionStorage.getItem(SESSION_KEY) || memorySessionId);
    } catch (_) {
        return !!memorySessionId;
    }
};

const resolveChannel = () => {
    try {
        if (readUserState().isAlliance) return CHANNEL_BY_USER_TYPE.alliance;
        const type = resolveUserType();
        return (type && CHANNEL_BY_USER_TYPE[String(type).toLowerCase()]) || undefined;
    } catch (_) {
        return undefined;
    }
};

const resolveLeadId = () => {
    try {
        const id = getLeadId();
        return id ? String(id) : undefined;
    } catch (_) {
        return undefined;
    }
};

/**
 * Pin common values once they are known (e.g. lead id after OTP verify).
 * Pass `undefined` for a key to go back to automatic resolution.
 * @param {{ lead_id?: string, channel?: string }} values
 */
export const setCommonProps = (values = {}) => {
    const next = { ...overrides };
    ['lead_id', 'channel'].forEach((key) => {
        if (!(key in values)) return;
        if (values[key] == null || values[key] === '') delete next[key];
        else next[key] = String(values[key]);
    });
    overrides = next;
};

export const resetCommonProps = () => {
    overrides = {};
};

/** @returns {{ lead_id?: string, session_id: string, channel?: string, platform: string }} */
export const getCommonProps = () => ({
    lead_id: overrides.lead_id || resolveLeadId(),
    session_id: getAnalyticsSessionId(),
    channel: overrides.channel || resolveChannel(),
    platform: PLATFORM,
});
